import React, { useState } from 'react';
import Slider from '@mui/material/Slider';
import MultiPick from './MultiPick';
import PickPopover, { SEARCH_FROM } from './PickPopover';
import { Row } from './settingsKit';
import { humanise } from './format';
import { switchesInTitles, withEntry } from './lensState';
import type { LensDocument, LensState, SqlParam } from './types';

type ParamControlsProps = { document: LensDocument; state: LensState; change?: (next: LensState) => void; all?: boolean; setOnly?: boolean; match?: string; only?: string; names?: Array<string>; labels?: Map<string, string> };

const isSqlParam = (param: LensDocument['params'][string]): param is SqlParam => 'sql' in param;
const SHOWN = 4;

export default function ParamControls({ document, state, change, all, setOnly, match = '', only, names, labels }: ParamControlsProps) {
  const [open, setOpen] = useState(false);
  const inTitles = switchesInTitles(document);
  const params = Object.entries(document.params).filter(([name, param]) => only ? name === only : names ? names.includes(name) : isSqlParam(param) && param.options.length > 0 && !inTitles.has(name));
  const matching = params.filter(([name, param]) => (param.label ?? humanise(name)).toLowerCase().includes(match.trim().toLowerCase()));
  const shown = setOnly
    ? params.filter(([name]) => state.params?.[name] !== undefined)
    : all
    ? [...matching.filter(([name]) => state.params?.[name] !== undefined), ...matching.filter(([name]) => state.params?.[name] === undefined)]
    : open ? params : params.filter(([name], index) => index < SHOWN || state.params?.[name] !== undefined);
  const hidden = all ? 0 : params.length - shown.length;
  return (
    <>
      {shown.map(([name, param]) => {
          const label = param.label ?? humanise(name);
          const chosen = state.params?.[name];
          const set = (value: string | undefined) => change?.({ ...state, params: withEntry(state.params, name, value) });
          if (!isSqlParam(param) && 'value' in param) {
            const value = chosen !== undefined && Number.isFinite(Number(chosen)) ? Number(chosen) : param.value;
            return (
              <Row key={name} label={label}>
                <Slider aria-label={label} disabled={!change} max={param.max} min={param.min} onChange={(_, next) => set(Number(next) === param.value ? undefined : String(next))} size="small" step={param.step ?? 1} sx={{ mx: 1 }} value={value} valueLabelDisplay="auto" />
                <span>{value}</span>
              </Row>
            );
          }
          if (isSqlParam(param) && param.multiple)
            return <MultiPick key={name} label={label} onPick={change ? (next) => set(next.join('|') || undefined) : undefined} options={param.options} picked={chosen ? chosen.split('|') : param.value ?? []} />;
          const options = 'measures' in param ? param.measures : 'columns' in param ? param.columns : isSqlParam(param) ? param.options : [];
          if (!options.length) return null;
          const optionLabel = (option: string) => isSqlParam(param) ? option : labels?.get(option) ?? document.measures[option]?.label ?? humanise(option);
          const current = chosen ?? options[0];
          const summary = optionLabel(current);

          return (
            <span className={`chip${chosen ? ' set' : ''}`} key={name} title={`${label}: ${summary}`}>
              {label}
              <span className="cv">{summary}</span>
              {change && options.length >= SEARCH_FROM ? (
                <PickPopover clearable={false} label={label} onPick={(next) => set(next[0] === options[0] ? undefined : next[0])} options={options.map((value) => ({ value, label: optionLabel(value) }))} overlay picked={[current]} single>{null}</PickPopover>
              ) : change ? (
                <select
                  aria-label={label}
                  onChange={(event) => set(event.target.value === options[0] ? undefined : event.target.value)}
                  value={current}
                >
                  {options.map((option) => (
                    <option key={option} value={option}>
                      {optionLabel(option)}
                    </option>
                  ))}
                </select>
              ) : null}
            </span>
          );
        })}
      {hidden > 0 ? (
        <button className="chip" onClick={() => setOpen(true)} type="button">
          More filters ({hidden})
        </button>
      ) : null}
    </>
  );
}
